import { useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { NotificationPreferenceScope } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useState } from "react";

import {
  MY_NOTIFICATION_PREFERENCE_CATALOG,
  RESET_MY_NOTIFICATION_PREFERENCES,
  RESET_ORGANIZATION_NOTIFICATION_PREFERENCES,
  SET_NOTIFICATION_PREFERENCES,
} from "./notificationPreferenceDocuments";
import {
  browserTimeZone,
  buildSavePayload,
  draftValue,
  NOTIFICATION_CHANNEL_EMAIL,
  useNotificationPreferenceDraft,
  type DraftCadenceChange,
  type DraftParentSwitch,
  type NotificationPreferenceDraftController,
} from "./useNotificationPreferenceDraft";

/**
 * State and behaviour for the notification preferences page: which sections are
 * open, both tabs' catalogs and drafts, and the write path behind Save, Reset and
 * the preset.
 *
 * Split out of the page component so the file that renders the layout is about
 * layout. These four hooks are the page's moving parts, and keeping them together
 * makes the one thing they share obvious — every one of them exists so a reader's
 * unsaved edits survive a tab switch, a failed refetch, or a reload.
 */

function useRefreshAfterWrite(setRefreshing: (value: boolean) => void) {
  const alerts = useAlerts();
  return useCallback(
    async (refetch: () => Promise<unknown>): Promise<boolean> => {
      setRefreshing(true);
      try {
        await refetch();
        return true;
      } catch {
        alerts.error(
          "Your change was saved, but this page could not be refreshed. Reload to see the current values."
        );
        return false;
      } finally {
        setRefreshing(false);
      }
    },
    [alerts, setRefreshing]
  );
}

/**
 * Which sections are open, remembered across visits.
 *
 * Everything starts expanded: a reader who came here to change one notification
 * should see the notifications, not five closed drawers to hunt through. After that
 * their own choice wins, because a collapse a page forgets is a page that argues
 * with them every time they arrive.
 *
 * Stored as an override map rather than a list of open sections, so a group that
 * ships later is expanded by default too instead of silently arriving collapsed.
 */
export function useExpandedSections() {
  const [collapsedByGroup, setCollapsedByGroup] = useLocalStorageState<Record<string, boolean>>(
    "notification-preferences-collapsed-sections",
    { defaultValue: {} }
  );

  const isExpanded = useCallback(
    (groupId: string) => !collapsedByGroup[groupId],
    [collapsedByGroup]
  );

  const toggle = useCallback(
    (groupId: string) => {
      setCollapsedByGroup((current) => ({ ...current, [groupId]: !current[groupId] }));
    },
    [setCollapsedByGroup]
  );

  return { isExpanded, toggle };
}

/**
 * Both tabs' catalogs and drafts, and which of them the visible tab is using.
 *
 * One hook because the two queries are the same question asked at two layers and
 * their drafts have to outlive a tab switch — a draft owned by the panel would throw
 * away unsaved edits the moment the reader looked at the other tab.
 */
export function usePreferenceCatalogs({
  tenantId,
  scope,
  canManageOrganizationDefaults,
}: {
  tenantId: string | undefined;
  scope: NotificationPreferenceScope;
  canManageOrganizationDefaults: boolean;
}) {
  const myQuery = useQuery(MY_NOTIFICATION_PREFERENCE_CATALOG, {
    variables: { tenantId: tenantId ?? "", scope: NotificationPreferenceScope.My },
    skip: !tenantId,
  });
  const organizationQuery = useQuery(MY_NOTIFICATION_PREFERENCE_CATALOG, {
    variables: { tenantId: tenantId ?? "", scope: NotificationPreferenceScope.OrgDefault },
    skip: !tenantId || !canManageOrganizationDefaults,
  });

  const myCatalog = myQuery.data?.myNotificationPreferenceCatalog;
  const organizationCatalog = organizationQuery.data?.myNotificationPreferenceCatalog;

  // Both drafts live here rather than inside the panel, so switching tabs cannot
  // throw away edits the reader has not saved yet.
  const myDraft = useNotificationPreferenceDraft(myCatalog);
  const organizationDraft = useNotificationPreferenceDraft(organizationCatalog);

  /*
    The permission is part of the question, not just of whether the tab is offered.

    This component survives a tenant change. A reader could leave it on the
    organization tab and come back under a tenant where they cannot manage defaults:
    the tab strip is hidden and the organization query is skipped, but a selection
    made on `scope` alone would still point at that skipped query — leaving the page
    loading forever with no visible way back to their own preferences.

    Derived rather than reset through an effect, so there is no first paint in which
    the two disagree.
  */
  const onOrganizationTab =
    scope === NotificationPreferenceScope.OrgDefault && canManageOrganizationDefaults;
  /*
    The scope everything else must use: the heading, the panel, and above all the
    save. Reading raw `scope` while the data came from the other tab is worse than the
    stranded loading state it replaced — a save would have written ORG_DEFAULT rows out
    of the reader's personal draft.
  */
  const effectiveScope = onOrganizationTab
    ? NotificationPreferenceScope.OrgDefault
    : NotificationPreferenceScope.My;
  const activeCatalog = onOrganizationTab ? organizationCatalog : myCatalog;
  const activeController = onOrganizationTab ? organizationDraft : myDraft;
  const activeQuery = onOrganizationTab ? organizationQuery : myQuery;

  const refetchBoth = useCallback(async () => {
    // An organization default is a layer under every member's view, so a change
    // to it moves the reader's own resolved values too — including this reader's.
    // Refetching only the tab that was saved would leave the other one stale in a
    // way the reader could see by clicking once.
    await myQuery.refetch();
    if (canManageOrganizationDefaults) {
      await organizationQuery.refetch();
    }
  }, [canManageOrganizationDefaults, myQuery, organizationQuery]);

  return {
    myQuery,
    organizationCatalog,
    onOrganizationTab,
    effectiveScope,
    activeCatalog,
    activeController,
    activeQuery,
    refetchBoth,
  };
}

/**
 * The cadence entry a save should carry, or null when the reader has not moved it.
 *
 * Resolved against the email channel master because that is the row cadence is
 * stored on, and it carries that switch's *current draft* position rather than
 * the server's: a reader who muted email and changed cadence in one visit has
 * made two edits to one row, and the save has to land both.
 *
 * Choosing a cadence therefore also *pins* the reader's email switch, which the
 * rest of this page is careful never to do — an untouched row stays absent so it
 * keeps following the organization. It is unavoidable here rather than an
 * oversight: cadence lives on that row, the row cannot exist without an `enabled`
 * value, and the value written is the one they are already inheriting, so nothing
 * changes for them today. What they give up is having a *later* change to the
 * organization's email switch reach them — a fair trade for an explicitly
 * personal choice, and reversible through "Reset preferences".
 */
function cadenceChangeForSave(
  controller: NotificationPreferenceDraftController,
  channelParentSwitches: readonly DraftParentSwitch[] | undefined,
  scope: NotificationPreferenceScope
): DraftCadenceChange | null {
  if (!controller.isCadenceDirty) {
    return null;
  }
  const emailParent = channelParentSwitches?.find(
    (parent) => parent.channel === NOTIFICATION_CHANNEL_EMAIL
  );
  if (!emailParent) {
    return null;
  }
  return {
    kind: emailParent.kind,
    channel: emailParent.channel,
    enabled: draftValue(controller.draft, emailParent.kind, emailParent.channel),
    cadence: controller.cadence,
    // Only ever this reader's own zone. On the organization tab the browser's zone
    // belongs to whichever admin happens to be saving, and sending it would set the
    // send hour for every colleague who has not chosen one — a company in California
    // mailing its Berlin engineer at 09:00 Pacific. The server refuses it on that
    // scope too; this is the half that stops the page asking.
    //
    // Cadence still goes, because a house cadence is a real organization policy. It is
    // the zone that is personal.
    timeZone: scope === NotificationPreferenceScope.My ? browserTimeZone() : undefined,
  };
}

/**
 * The page's three writes, each reporting an error the same way.
 *
 * Grouped because they share exactly one thing — every failure becomes an alert
 * rather than an unhandled rejection — and because keeping them beside the
 * handlers that call them pushed the hook that owns those handlers past the
 * function-length cap.
 */
function usePreferenceMutations() {
  const alerts = useAlerts();
  const onError = useCallback(
    (error: { message: string }) => alerts.error(error.message),
    [alerts]
  );

  const [setPreferences, { loading: saving }] = useMutation(SET_NOTIFICATION_PREFERENCES, {
    onError,
  });
  const [resetPreferences, { loading: resetting }] = useMutation(
    RESET_MY_NOTIFICATION_PREFERENCES,
    { onError }
  );
  const [resetOrganizationPreferences, { loading: applyingPreset }] = useMutation(
    RESET_ORGANIZATION_NOTIFICATION_PREFERENCES,
    { onError }
  );

  return {
    alerts,
    setPreferences,
    saving,
    resetPreferences,
    resetting,
    resetOrganizationPreferences,
    applyingPreset,
  };
}

export function usePreferenceActions({
  tenantId,
  scope,
  activeController,
  channelParentSwitches,
  refetchBoth,
  refetchMine,
  closeReset,
  closePreset,
}: {
  tenantId: string | undefined;
  scope: NotificationPreferenceScope;
  activeController: NotificationPreferenceDraftController;
  /** The active tab's channel switches, which is where a cadence edit is stored. */
  channelParentSwitches: readonly DraftParentSwitch[] | undefined;
  refetchBoth: () => Promise<void>;
  /** Only the reader's own tab: a personal reset cannot move an organization default. */
  refetchMine: () => Promise<unknown>;
  closeReset: () => void;
  closePreset: () => void;
}) {
  const {
    alerts,
    setPreferences,
    saving,
    resetPreferences,
    resetting,
    resetOrganizationPreferences,
    applyingPreset,
  } = usePreferenceMutations();

  /**
   * True from the moment a write leaves until its refetch has settled.
   *
   * Every switch on the page is blocked for that whole window, which is a
   * deliberate choice over the alternative of merging edits made mid-flight. The
   * draft is re-seeded from whatever the refetch returns, so an edit made while a
   * save was in the air was silently thrown away and the toast still said saved.
   * Preserving it instead would mean diffing the reader's edit against a baseline
   * that may already contain the value they just saved — a flip made and unmade
   * during the flight becomes indistinguishable from no change, and would be
   * dropped anyway. Blocking loses nothing except a second of interactivity, and
   * it cannot lose an edit.
   */
  const [refreshing, setRefreshing] = useState(false);
  const refreshAfterWrite = useRefreshAfterWrite(setRefreshing);

  // `saving`/`resetting` end when the mutation resolves, which is *before* the
  // refetch that re-seeds the draft. Leaving the switches live for that gap would
  // reopen the very hole this closes, so the refresh is tracked explicitly rather
  // than inferred from the mutation.
  const mutating = saving || resetting || applyingPreset || refreshing;

  const handleSave = useCallback(async () => {
    if (!tenantId || !activeController.isDirty) {
      return;
    }
    const cadenceChange = cadenceChangeForSave(activeController, channelParentSwitches, scope);
    const result = await setPreferences({
      variables: {
        tenantId,
        input: buildSavePayload(activeController.dirty, scope, cadenceChange),
      },
    });
    if (!result.data) {
      return;
    }
    if (!(await refreshAfterWrite(refetchBoth))) {
      return;
    }
    alerts.success("Notification preferences saved.");
  }, [
    activeController,
    alerts,
    channelParentSwitches,
    refetchBoth,
    refreshAfterWrite,
    scope,
    setPreferences,
    tenantId,
  ]);

  const handleReset = useCallback(async () => {
    if (!tenantId) {
      return;
    }
    const result = await resetPreferences({ variables: { tenantId } });
    closeReset();
    if (!result.data) {
      return;
    }
    // Only the reader's own rows moved, so the organization tab is still accurate.
    if (!(await refreshAfterWrite(refetchMine))) {
      return;
    }
    alerts.success("Your notification preferences were reset.");
  }, [alerts, closeReset, refetchMine, refreshAfterWrite, resetPreferences, tenantId]);

  const handleApplyPreset = useCallback(async () => {
    if (!tenantId) {
      closePreset();
      return;
    }
    // A delete, not a bulk write of the recommended values. Those values are what
    // an organization with no rows of its own already resolves to, so writing them
    // would change nothing today and then freeze this organization out of every
    // later change Encamp makes to its own recommendations — invisibly, because the
    // page looks identical either way.
    const result = await resetOrganizationPreferences({ variables: { tenantId } });
    closePreset();
    if (!result.data) {
      return;
    }
    if (!(await refreshAfterWrite(refetchBoth))) {
      return;
    }
    alerts.success("Your organization's defaults are back to Encamp's recommended posture.");
  }, [alerts, closePreset, refetchBoth, refreshAfterWrite, resetOrganizationPreferences, tenantId]);
  return {
    mutating,
    saving,
    resetting,
    applyingPreset,
    handleSave,
    handleReset,
    handleApplyPreset,
  };
}
